import { useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import { Status_Enum } from '@weave/schema-gen-ts/dist/schemas/appointments/v3/model.pb';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { AppointmentStatusComponents } from '@frontend/appointment-status';
import { AutoMessageDetailAppointmentEvent } from '@frontend/auto-message-detail-appointment-event';
import { useDevToolsStore } from '@frontend/devtools';
import { useTranslation } from '@frontend/i18n';
import { useScopedAppFlagStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { ExtendedAppointment, useGetIntegrationDetails, useLocationTimezone } from '../../../hooks';
import { useAppointmentEventCardShallowStore } from '../../../stores/use-appointment-event-card-store';
import {
  OverlappingEventIndicator,
  OverlappingEventIndicatorVariantType,
} from '../../calendar-view/calendar-view-v3/overlapping-event-indicator';
import { EventCardComponentProps } from '../../calendar-view/providers';
import { prepareAppointmentInfo } from '../helpers';
import { MotionText } from '../motion-text';
import { AppointmentDetails } from './appointment-details';
import { NotesSection } from './notes-section';
import { PersonDetails } from './person-details';

interface Props {
  appointment: ExtendedAppointment;
  closeAppointmentDetailsModal?: () => void;
  eventId: string;
  locationName: string;
  referenceId?: string;
}

export const AppointmentEventCardPreview = ({
  name,
  duration,
  status,
  overlappingIndicatorVariant,
  isYesterdayEvent,
}: {
  name: string;
  duration: string;
  status?: Status_Enum;
} & Pick<EventCardComponentProps, 'overlappingIndicatorVariant' | 'isYesterdayEvent'>) => {
  const { isScheduleV3FlagEnabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();
  const isDebugModeOn = useDevToolsStore((state) => state.options.isDebugModeOn);
  const isNonAttendedEvent = status ? [Status_Enum.CANCELED, Status_Enum.NO_SHOW].includes(status) : false;
  const isNonAttendedV3Event = isScheduleV3FlagEnabled && isNonAttendedEvent;

  const overlappingIndicator = (variant: OverlappingEventIndicatorVariantType) => (
    <OverlappingEventIndicator
      variant={variant}
      isYesterdayEvent={isYesterdayEvent}
      visible={overlappingIndicatorVariant === variant}
    />
  );

  return (
    <div css={{ position: 'relative' }}>
      <div css={{ display: 'flex', alignItems: 'center' }}>
        <MotionText size='medium' weight='bold' css={textStyle(isNonAttendedV3Event)}>
          {name}
        </MotionText>
        {overlappingIndicator('dot')}
      </div>
      <div css={{ display: 'flex', alignItems: 'center' }}>
        <MotionText size='small' color='light' css={textStyle(isNonAttendedV3Event)}>
          {duration}
        </MotionText>
        {overlappingIndicator('icon')}
      </div>
      {overlappingIndicator('full')}
      {isDebugModeOn && !!status && (
        <AppointmentStatusComponents.StatusChip
          status={status}
          isIconOnly
          css={{
            position: 'absolute',
            top: theme.spacing(0.25),
            right: theme.spacing(-1.25),
            height: 20,
            width: 20,
          }}
        />
      )}
    </div>
  );
};

const textStyle = (strikeThrough: boolean) =>
  css({
    textDecoration: strikeThrough ? 'line-through' : 'none',
    flex: 'unset',
  });

export const AppointmentEventCard = ({
  appointment,
  closeAppointmentDetailsModal,
  eventId,
  locationName,
  referenceId = '',
}: Props) => {
  const { t } = useTranslation('schedule');
  const { getLocationTimezoneAbbr } = useLocationTimezone();
  const appointmentInfo = useMemo(() => {
    const appointmentInfoToPrepare = appointment ? [appointment] : [];
    return prepareAppointmentInfo(appointmentInfoToPrepare ?? [], eventId, getLocationTimezoneAbbr);
  }, [appointment, eventId, getLocationTimezoneAbbr]);

  const { getCustomizationFlagValue } = useScopedAppFlagStore();
  const hasApptReminderFlag = getCustomizationFlagValue('apptreminders-automated');

  const { setAppointmentDetails, setIsIntegratedOffice, setCloseAppointmentDetailsModal } =
    useAppointmentEventCardShallowStore(
      'setAppointmentDetails',
      'setIsIntegratedOffice',
      'setCloseAppointmentDetailsModal'
    );

  const { isIntegratedOffice, isLoading } = useGetIntegrationDetails({
    selectedLocationId: appointmentInfo?.locationId ?? '',
  });
  useEffect(() => {
    if (appointment) {
      setAppointmentDetails(appointment);
    }
    setIsIntegratedOffice(isIntegratedOffice);
    !!closeAppointmentDetailsModal && setCloseAppointmentDetailsModal(closeAppointmentDetailsModal);
  }, [isIntegratedOffice, appointment]);

  useEffect(() => {
    return () => setAppointmentDetails(null);
  }, []);

  if (isLoading) {
    return <AppointmentEventCardPreview name={t('Loading...')} duration={t('Loading...')} />;
  }

  const autoMessageRecordId = !!referenceId ? referenceId : appointmentInfo?.id ?? '';

  if (!appointmentInfo) {
    return null;
  }
  return (
    <>
      <PersonDetails
        locationId={appointmentInfo.locationId}
        personInfo={appointmentInfo.personInfo}
        appointmentId={appointmentInfo.appointmentDetails.referenceId}
        serviceableIds={appointmentInfo?.petInfo.serviceableIds || []}
        closeAppointmentDetailsModal={closeAppointmentDetailsModal}
      />
      <AppointmentDetails
        appointmentDetails={appointmentInfo.appointmentDetails}
        locationName={locationName}
        locationId={appointmentInfo.locationId}
        appointmentId={eventId}
        isIntegratedOffice={isIntegratedOffice}
      />
      {hasApptReminderFlag && (
        <AutoMessageDetailAppointmentEvent
          appointmentId={autoMessageRecordId}
          appointmentLocationId={appointmentInfo.locationId}
          closeAppointmentDetailsModal={closeAppointmentDetailsModal}
          person={appointmentInfo.personInfo}
        />
      )}
      {appointmentInfo.personInfo.householdId && <NotesSection householdId={appointmentInfo.personInfo.householdId} />}
    </>
  );
};
